/**
 * Native AR launch helpers (Prompt 3B §17/§19) - the fallback used only when
 * first-party WebXR is unavailable or a genuine WebXR start fails. Never a
 * WebXR owner itself; never persists a launch URL as canonical identity.
 *
 * Kept dependency-free (no model-viewer/Three/Cesium) so importing this
 * module never affects the ordinary Studio/Earth bundle.
 */

export function isAppleMobileUA(ua = navigator.userAgent || "") {
  return /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isAndroidUA(ua = navigator.userAgent || "") {
  return /Android/i.test(ua);
}

function validDeliveryUrl(value) {
  try { return ['https:', 'http:'].includes(new URL(value).protocol); } catch { return false; }
}

/**
 * iOS/iPadOS Quick Look - requires a real USDZ URL. Must be called
 * synchronously within the triggering user gesture. Creates a throwaway
 * `<a rel="ar">` anchor, clicks it, and removes it immediately after -
 * nothing is persisted.
 */
export function launchIosQuickLook(usdzUrl) {
  if (!usdzUrl || !validDeliveryUrl(usdzUrl) || !isAppleMobileUA()) return false;
  const anchor = document.createElement("a");
  anchor.rel = "ar";
  anchor.href = usdzUrl;
  anchor.style.display = "none";
  const image = document.createElement("img");
  image.alt = "";
  anchor.appendChild(image);
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => anchor.remove(), 0);
  return true;
}

/**
 * Android Scene Viewer - requires a real absolute GLB URL. The intent's
 * browser_fallback_url returns to the current/canonical page rather than an
 * arbitrary URL, so a user who can't open the intent lands back where they
 * started.
 */
export function launchAndroidSceneViewer(glbUrl, {
  fallbackUrl,
  placementProfile = "floor",
  resizable = false,
} = {}) {
  if (!glbUrl || !validDeliveryUrl(glbUrl) || !isAndroidUA()) return false;
  if (fallbackUrl && !validDeliveryUrl(fallbackUrl)) return false;
  const file = encodeURIComponent(glbUrl);
  const fallback = encodeURIComponent(fallbackUrl || window.location.href);
  const vertical = ["wall", "window_covering"].includes(String(placementProfile || "").toLowerCase());
  const query = [
    "mode=ar_preferred",
    `file=${file}`,
    `resizable=${resizable ? "true" : "false"}`,
    ...(vertical ? ["enable_vertical_placement=true"] : []),
  ].join("&");
  window.location.href = `intent://arvr.google.com/scene-viewer/1.2?${query}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${fallback};end;`;
  return true;
}
